import {Spinner} from "@heroui/spinner";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, Link, useParams} from '@tanstack/react-router'
import {Calendar, ChevronLeft, Clock, ExternalLink, Mail, MapPin, Trophy, Users} from 'lucide-react'

import heroBg from "../landing/assets/tournament_detail_hero.webp";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import StarIcon from "../landing/icons/StarIcon.tsx";
import VerifiedSealIcon from "../landing/icons/VerifiedSealIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import EventCountdown from "../components/EventCountdown.tsx";
import services from "../services";

export const Route = createFileRoute('/event/$uid')({
  component: RouteComponent,
})

const PHASE_CHIP: Record<string, {label: string; cls: string}> = {
  register:         {label: 'Registration Open', cls: 'bg-[#14a653] text-white'},
  register_live:    {label: 'Started · Registration Open', cls: 'bg-[#14a653] text-white'},
  full:             {label: 'Tournament Full', cls: 'bg-[#d9822b] text-white'},
  generating_draws: {label: 'Generating Draws', cls: 'bg-[#2563eb] text-white'},
  happening:        {label: 'Live', cls: 'bg-[#14a653] text-white'},
  finished:         {label: 'Completed', cls: 'bg-[#475569] text-white'},
  not_open:         {label: 'Opening Soon', cls: 'bg-[#475569] text-white'},
  closed:           {label: 'Closed', cls: 'bg-[#475569] text-white'},
}

function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`
}

function fmtDateLong(iso?: string | null) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', {weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'})
}

function fmtDateShort(iso?: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.toLocaleDateString('en-US', {weekday: 'short'})} ${ordinal(d.getDate())}, ${d.toLocaleDateString('en-US', {month: 'short'})}`
}

function fmtTime(iso?: string | null) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})
}

function fmtTimeRange(startIso?: string | null, endIso?: string | null) {
  if (!startIso) return ''
  const start = fmtTime(startIso)
  const end = endIso ? fmtTime(endIso) : ''
  return end ? `${start} - ${end}` : start
}

function RouteComponent() {
  const {uid} = useParams({from: '/event/$uid'})

  const eventQuery = useQuery({
    queryKey: ['public-event', uid],
    queryFn:  () => services.publicEvent.show(uid),
    enabled:  !!uid,
  })

  if (eventQuery.isLoading) {
    return (
      <main className="min-h-screen bg-[#0a1530]">
        <LandingNav />
        <div className="flex justify-center pt-24"><Spinner size="lg" color="white" /></div>
      </main>
    )
  }

  const e = eventQuery.data
  if (!e) {
    return (
      <main className="min-h-screen bg-[#0a1530] text-white">
        <LandingNav />
        <div className="mx-auto max-w-[860px] px-6 py-24 text-center">
          <Trophy className="mx-auto mb-4 text-white/40" size={48} />
          <h1 className="text-2xl font-black">Event not found</h1>
          <p className="mt-2 text-white/60">This tournament or league is not available.</p>
        </div>
        <FooterSection />
      </main>
    )
  }

  const phase = PHASE_CHIP[e.registration_phase] ?? PHASE_CHIP.closed
  const fee = e.entry_amount ? `${e.entry_currency_code || ''} ${(e.entry_amount / 100).toLocaleString()}`.trim() : 'Free'
  const location = [e.venue, e.city].filter(Boolean).join(', ')
  const poster = e.public_card_image_url || e.banner_url
  const registerUrl = `/e/${e.slug || e.uid}/`
  const isExternal = e.registration_channel === 'external' && !!e.registration_external_url

  const addToCalendar = () => {
    if (!e.starts_at) return
    const start = new Date(e.starts_at)
    const end = e.ends_at ? new Date(e.ends_at) : new Date(start.getTime() + 2 * 60 * 60 * 1000)
    const z = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
    const gcal = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(e.title)}&dates=${z(start)}/${z(end)}&location=${encodeURIComponent(location)}`
    window.open(gcal, '_blank', 'noopener')
  }

  const openMaps = () => {
    if (!location) return
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`, '_blank', 'noopener')
  }

  const RegisterCta = ({block = false}: {block?: boolean}) => {
    const cls = `flex items-center gap-3 rounded-full bg-[#EDEEF4] py-1 pl-6 pr-1 font-medium text-[#082F8A] shadow-lg transition hover:bg-white ${block ? 'w-full justify-between' : 'w-fit'}`
    const inner = (
      <>
        <span className="text-lg font-medium tracking-wide">Register Now</span>
        <div className="rounded-full bg-[#082F8A]"><ArrowUpRightIcon className="text-white" size={40} /></div>
      </>
    )
    if (isExternal) {
      return <a href={e.registration_external_url!} target="_blank" rel="noreferrer" className={cls}>{inner}</a>
    }
    return <Link to={registerUrl as any}>
      <span className={cls}>{inner}</span>
    </Link>
  }

  return (
    <main className="min-h-screen bg-[#EDEEF4] font-inter text-[#071a5f]">
      {/* Hero */}
      <section className="relative p-3 sm:p-4">
        <div className="relative flex min-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-[28px] text-white sm:min-h-[calc(100vh-2rem)] sm:rounded-4xl">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat saturate-[0.87]" style={{backgroundImage: `url(${heroBg})`}} />
          <div className="absolute inset-0 bg-black/30" />

          <div className="relative z-10 flex flex-1 flex-col">
            <LandingNav />

            <div className="container mx-auto flex flex-1 flex-col gap-8 px-6 py-8 lg:flex-row lg:gap-12 lg:px-12 lg:py-10">
              {/* LEFT: tournament info */}
              <div className="flex flex-1 flex-col">
                <Link to="/find-tournaments/" className="inline-flex w-fit items-center gap-1 text-sm font-medium text-white/70 transition hover:text-white">
                  <ChevronLeft size={16} /> Back to Tournaments
                </Link>

                <div className="mt-8 mb-5 flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 backdrop-blur-md">
                  <StarIcon className="text-white" size={14} />
                  <span className="text-sm font-medium text-white"><EventCountdown entryEndsAt={e.entry_ends_at} startsAt={e.starts_at} fallback={phase.label} /></span>
                </div>

                <h1 className="font-bebas text-[clamp(36px,5vw,68px)] font-bold uppercase leading-[0.95] tracking-wide">
                  {e.title}
                </h1>

                {e.organizer ? (
                  <div className="mt-6 flex items-center gap-3">
                    <Avatar src={e.organizer.image} fallback={e.organizer.name} size={42} />
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white">{e.organizer.name}</div>
                      {(e.venue || e.city) ? (
                        <div className="truncate text-xs text-white/70">{[e.venue, e.city].filter(Boolean).join(', ')}</div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {e.entry_amount ? (
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-bebas text-4xl font-bold leading-none">{fee}</span>
                    <span className="text-sm text-white/70">per player</span>
                  </div>
                ) : null}

                <div className="my-7 h-px bg-white/15" />

                <div className="grid gap-5 sm:grid-cols-2">
                  <InfoLabelValue icon={<Calendar size={18} />} label="Date" value={fmtDateLong(e.starts_at) || 'TBC'} />
                  <InfoLabelValue icon={<Clock size={18} />} label="Time" value={fmtTimeRange(e.starts_at, e.ends_at) || 'TBC'} />
                  <InfoLabelValue icon={<Users size={18} />} label="Gender Category" value="Open" />
                  <InfoLabelValue icon={<Trophy size={18} />} label="Tournament Categories" value={e.grade_default ? `Open ${e.grade_default}` : 'Open'} />
                </div>

                <div className="mt-auto pt-10">
                  {e.organizer ? (
                    <div className="flex items-center gap-2 text-sm text-white/80">
                      <Avatar src={e.organizer.image} fallback={e.organizer.name} size={20} />
                      <span>Organized by <span className="font-semibold text-white">{e.organizer.name}</span></span>
                    </div>
                  ) : null}
                  {e.organizer ? (
                    <div className="mt-1 text-xs text-white/55">Submitted by {e.organizer.name}</div>
                  ) : null}
                </div>
              </div>

              {/* RIGHT: poster */}
              {poster ? (
                <div className="flex items-center justify-center lg:w-[460px] lg:shrink-0">
                  <div className="w-full overflow-hidden rounded-3xl border border-white/15 bg-white/5 shadow-2xl">
                    <img src={poster} alt={e.title} className="aspect-[4/5] w-full object-cover" />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Body — the 2-col split keys off the page's own width (container query), not the viewport,
          so embedded/narrow contexts stack instead of crushing the main column. */}
      <div className="@container">
      <div className="container mx-auto grid gap-6 px-4 py-10 @4xl:grid-cols-[1fr_340px] lg:px-12">
        <div className="space-y-5">
          <Section title="About This Tournament">
            {e.registration_about ? (
              <p className="whitespace-pre-line text-sm leading-relaxed text-[#3c4d77]">{e.registration_about}</p>
            ) : (
              <p className="text-sm text-[#7c8aa9]">The organizer has not added a description yet.</p>
            )}
          </Section>

          <Section title="Registration & Contact">
            <h4 className="mb-1 text-sm font-black text-[#06195f]">How to Register</h4>
            <p className="mb-4 text-sm leading-relaxed text-[#3c4d77]">
              {isExternal
                ? 'The organizer collects registrations through their own link below.'
                : 'Register through ScorePal using the button below. You can sign in or create an account during checkout.'}
            </p>
            {isExternal ? (
              <a href={e.registration_external_url!} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#1faa53]">
                Register Externally <ArrowUpRightIcon className="text-white" size={18} />
              </a>
            ) : (
              <Link to={registerUrl as any} className="inline-flex items-center gap-2 rounded-full bg-[#082f8a] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#06266f]">
                Register on ScorePal <ArrowUpRightIcon className="text-white" size={18} />
              </Link>
            )}

            {e.organizer ? (
              <div className="mt-6 border-t border-[#eef1f8] pt-4">
                <h4 className="mb-2 text-sm font-black text-[#06195f]">Contact Information</h4>
                <p className="text-sm text-[#3c4d77]">Organized by <span className="font-semibold text-[#2168ff]">{e.organizer.name}</span></p>
              </div>
            ) : null}
          </Section>

          {e.prize ? (
            <Section title="Prizes & Awards">
              <div className="flex items-start gap-3 rounded-2xl border border-[#fde9c9] bg-[#fff8eb] p-4">
                <Trophy size={26} className="shrink-0 text-[#d9822b]" />
                <p className="whitespace-pre-line text-sm font-medium text-[#3c4d77]">{e.prize}</p>
              </div>
            </Section>
          ) : null}

          {(e.venue || e.city || e.organizer) ? (
            <Section title="Venue Information">
              {/* Header: club avatar + name/city */}
              <div className="flex items-start gap-3">
                {e.organizer?.image ? (
                  <img src={e.organizer.image} alt="" className="h-14 w-14 shrink-0 rounded-xl border border-[#e3e8f5] object-cover" />
                ) : (
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-[#eef4ff] text-base font-black text-[#2168ff]">
                    {((e.organizer?.name || e.venue || '?') as string).charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-base font-black text-[#06195f]">{e.organizer?.name || e.venue || 'Venue'}</div>
                  {e.city ? <div className="text-sm text-[#5770ab]">{e.city}</div> : null}
                </div>
              </div>

              {/* Address + action buttons */}
              <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="text-xs font-black text-[#06195f]">Address</div>
                  <div className="text-sm text-[#3c4d77]">{location || 'TBC'}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {location ? (
                    <button onClick={openMaps} className="inline-flex items-center gap-2 rounded-full bg-[#082f8a] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#06266f]">
                      <MapPin size={16} /> Get Directions
                    </button>
                  ) : null}
                  {e.organizer?.uid ? (
                    <Link to={`/profiles/${e.organizer.uid}/` as any} className="inline-flex items-center gap-2 rounded-full border border-[#cdd9f2] px-4 py-2 text-sm font-bold text-[#082f8a] transition hover:bg-[#f5f7fc]">
                      <ExternalLink size={16} /> View Club Profile
                    </Link>
                  ) : null}
                </div>
              </div>

              {/* Contact pills (only render rows we have data for) */}
              {e.organizer?.email ? (
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <ContactPill icon={<Mail size={14} />} label={`Email: ${e.organizer.email}`} href={`mailto:${e.organizer.email}`} />
                </div>
              ) : null}

              {/* Map with floating Get Directions */}
              {location ? (
                <div className="relative mt-4 overflow-hidden rounded-2xl border border-[#dfe6f5]">
                  <iframe className="h-64 w-full"
                          title={`Map of ${location}`}
                          src={`https://www.google.com/maps?q=${encodeURIComponent(location)}&output=embed`}
                          loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                  <button onClick={openMaps}
                          className="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full bg-[#1f2a52]/85 px-3 py-1.5 text-xs font-bold text-white backdrop-blur transition hover:bg-[#1f2a52]">
                    <MapPin size={14} /> Get Directions
                  </button>
                </div>
              ) : null}
            </Section>
          ) : null}
        </div>

        <aside className="space-y-4">
          <div className="rounded-3xl border border-[#dfe6f5] bg-white p-5 shadow-[0_8px_22px_rgba(20,40,90,0.08)]">
            <SidebarRow icon={<Calendar size={16} className="text-white" />} label={fmtDateShort(e.starts_at) || 'Date TBC'} />
            <SidebarRow icon={<Clock size={16} className="text-white" />} label={fmtTime(e.starts_at) || 'Time TBC'} />
            <SidebarRow icon={<MapPin size={16} className="text-white" />} label={location || 'Location TBC'} />
            <SidebarRow icon={<Trophy size={16} className="text-white" />} label={fee} last />
          </div>

          <button onClick={addToCalendar}
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-[#dfe6f5] bg-white py-3 text-sm font-bold text-[#082f8a] transition hover:bg-[#f5f7fc]">
            <Calendar size={16} /> Add to Calendar
          </button>

          <div className="rounded-3xl border border-[#cdd9f2] bg-[#eef4ff] p-5">
            <div className="mb-2 flex items-center gap-2 text-[#082f8a]">
              <VerifiedSealIcon className="text-[#082f8a]" size={20} />
              <span className="text-base font-black text-[#06195f]">ScorePal Verified</span>
            </div>
            <p className="text-sm leading-relaxed text-[#3c4d77]">
              Managed with ScorePal for real-time match tracking, registrations, and a verified results record.
            </p>
          </div>

          <RegisterCta block />
        </aside>
      </div>
      </div>

      <FooterSection />
    </main>
  )
}

function ContactPill({icon, label, href, iconClass = 'text-[#2168ff]'}: {icon: React.ReactNode; label: string; href?: string; iconClass?: string}) {
  const content = (
    <span className="flex items-center gap-2 rounded-xl bg-[#f5f7fc] px-3 py-2.5 text-sm font-medium text-[#27325c]">
      <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white ${iconClass}`}>{icon}</span>
      <span className="truncate">{label}</span>
    </span>
  )
  return href ? <a href={href} className="block min-w-0">{content}</a> : <div className="min-w-0">{content}</div>
}

function InfoLabelValue({icon, label, value}: {icon: React.ReactNode; label: string; value: string}) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-white">{icon}</span>
      <div className="min-w-0">
        <div className="text-xs font-medium text-white/65">{label}</div>
        <div className="truncate text-base font-bold text-white">{value}</div>
      </div>
    </div>
  )
}

function Avatar({src, fallback, size = 40}: {src?: string | null; fallback?: string; size?: number}) {
  if (src) {
    return <img src={src} alt="" style={{height: size, width: size}} className="shrink-0 rounded-full border border-white/20 object-cover" />
  }
  return (
    <div style={{height: size, width: size}}
         className="grid shrink-0 place-items-center rounded-full bg-white/15 text-xs font-black text-white">
      {(fallback || '?').charAt(0).toUpperCase()}
    </div>
  )
}

function Section({title, children}: {title: string; children: React.ReactNode}) {
  return (
    <div className="rounded-3xl border border-[#dfe6f5] bg-white p-5 shadow-[0_8px_22px_rgba(20,40,90,0.08)] lg:p-6">
      <h2 className="mb-3 text-lg font-black text-[#06195f]">{title}</h2>
      {children}
    </div>
  )
}

function SidebarRow({icon, label, last = false}: {icon: React.ReactNode; label: string; last?: boolean}) {
  return (
    <div className={`flex items-center gap-3 ${last ? '' : 'mb-3 border-b border-[#eef1f8] pb-3'}`}>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#082f8a]">{icon}</span>
      <span className="truncate text-sm font-semibold text-[#27325c]">{label}</span>
    </div>
  )
}
